import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { schoolAffiliationCorrectionAPI } from '../../services/api';
import { formatAreaText } from '../../utils/areaHelpers';

const { Title, Text } = Typography;
const { TextArea, Search } = Input;

const STATUS_OPTIONS = [
  { value: 'pending', label: '待审核', color: 'processing' },
  { value: 'approved', label: '已通过', color: 'success' },
  { value: 'rejected', label: '已驳回', color: 'error' },
  { value: 'cancelled', label: '已取消', color: 'default' },
  { value: 'revoked', label: '已撤回', color: 'warning' }
];

const REVIEW_OPTIONS = [
  { value: 'approved', label: '审核通过' },
  { value: 'rejected', label: '审核驳回' }
];

const ApplicantIdentity = ({ record, compact = false }) => {
  const realName = String(record?.applicant_real_name || '').trim();
  const accountLabel = record?.applicant_username || record?.applicant_name || '-';
  return (
    <div>
      <div>{realName || <Tag color="warning">未填写真实姓名</Tag>}</div>
      {!compact && <Text type="secondary">账号：{accountLabel}</Text>}
    </div>
  );
};

const SchoolAffiliationCorrectionReview = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [keyword, setKeyword] = useState('');
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewingRecord, setReviewingRecord] = useState(null);
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [migrationPreviewLoading, setMigrationPreviewLoading] = useState(false);
  const [reviewMigrationMode, setReviewMigrationMode] = useState('none');
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [revokingRecord, setRevokingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [revokeForm] = Form.useForm();

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await schoolAffiliationCorrectionAPI.getAll({
        status: statusFilter,
        keyword: keyword.trim(),
        limit: 100
      });

      if (response.data.success) {
        setRecords(response.data.data || []);
      } else {
        message.error(response.data.message || '获取入校申请失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '获取入校申请失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, [statusFilter]);

  const openReviewModal = async (record) => {
    const requestedMode = record.migration_mode === 'move_teacher_classes' ? 'move_teacher_classes' : 'none';
    setReviewingRecord(record);
    setMigrationPreview(null);
    setReviewMigrationMode(requestedMode);
    setSelectedClassIds([]);
    form.setFieldsValue({
      status: 'approved',
      resolved_school_name: record.requested_school_name,
      migration_mode: requestedMode,
      review_note: ''
    });
    setReviewModalOpen(true);
    try {
      setMigrationPreviewLoading(true);
      const response = await schoolAffiliationCorrectionAPI.getMigrationPreview(record.id);
      if (response.data.success) {
        const preview = response.data.data || null;
        setMigrationPreview(preview);
        const duplicateIds = new Set(
          (preview?.duplicateClasses || []).map((item) => String(item.sourceClassId || item.source_class_id))
        );
        const eligibleIds = (preview?.classes || [])
          .filter((item) => item.migrationEligible !== false && !duplicateIds.has(String(item.id)))
          .map((item) => String(item.id));
        const requestedIds = (record.requested_class_ids || []).map(String);
        setSelectedClassIds(requestedIds.length > 0
          ? requestedIds.filter((id) => eligibleIds.includes(id))
          : eligibleIds);
      }
    } catch (error) {
      message.warning(error.response?.data?.message || '迁移预览加载失败，默认只变更用户归属');
    } finally {
      setMigrationPreviewLoading(false);
    }
  };

  const handleReview = async (values) => {
    if (!reviewingRecord) {
      return;
    }

    if (values.status === 'approved' && values.migration_mode === 'move_teacher_classes' && selectedClassIds.length === 0) {
      message.warning('请选择至少一个可迁移班级，或改为不迁移班级数据');
      return;
    }

    try {
      setSaving(true);
      const response = await schoolAffiliationCorrectionAPI.review(reviewingRecord.id, {
        ...values,
        class_ids: values.migration_mode === 'move_teacher_classes' ? selectedClassIds : []
      });

      if (response.data.success) {
        message.success(response.data.message || '审核完成');
        setReviewModalOpen(false);
        setReviewingRecord(null);
        setReviewMigrationMode('none');
        setSelectedClassIds([]);
        form.resetFields();
        fetchRecords();
      } else {
        message.error(response.data.message || '审核失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '审核失败');
    } finally {
      setSaving(false);
    }
  };

  const openRevokeModal = (record) => {
    setRevokingRecord(record);
    revokeForm.resetFields();
    setRevokeModalOpen(true);
  };

  const handleRevoke = async (values) => {
    if (!revokingRecord) return;
    try {
      setSaving(true);
      const response = await schoolAffiliationCorrectionAPI.revoke(revokingRecord.id, {
        revoke_note: values.revoke_note
      });
      if (response.data.success) {
        message.success('已撤回审核');
        setRevokeModalOpen(false);
        setRevokingRecord(null);
        revokeForm.resetFields();
        fetchRecords();
      } else {
        message.error(response.data.message || '撤回失败');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '撤回失败');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: '提交时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-')
    },
    {
      title: '教师身份',
      key: 'applicant',
      render: (_, record) => (
        <ApplicantIdentity record={record} />
      )
    },
    {
      title: '当前学校',
      key: 'current_school_name',
      render: (_, record) => record.current_school_name || '-'
    },
    {
      title: '目标学校',
      dataIndex: 'requested_school_name',
      key: 'requested_school_name'
    },
    {
      title: '目标地区',
      key: 'requested_region',
      render: (_, record) =>
        formatAreaText(record.requested_province, record.requested_city, record.requested_county) || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const config = STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];
        return <Tag color={config.color}>{config.label}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_, record) => {
        if (record.status === 'pending') {
          return (
            <Button type="link" onClick={() => openReviewModal(record)}>
              审核
            </Button>
          );
        }
        if (record.status === 'approved') {
          return (
            <Button type="link" danger onClick={() => openRevokeModal(record)}>
              撤回
            </Button>
          );
        }
        return <Text type="secondary">已处理</Text>;
      }
    }
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card>
        <Title level={4} style={{ marginTop: 0 }}>
          入校申请审核
        </Title>
        <Alert
          type="warning"
          showIcon
          message="审核说明"
          description="学校二维码提交的申请会自动带上目标学校；审核通过默认只增加申请人的目标学校归属，不退出原学校和原班级。"
        />
      </Card>

      <Card
        extra={
          <Space>
            <Search
              placeholder="搜索申请人、当前学校、目标学校、省市县"
              allowClear
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onSearch={fetchRecords}
              style={{ width: 320 }}
            />
            <Select
              style={{ width: 140 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: '', label: '全部状态' },
                ...STATUS_OPTIONS.map((item) => ({ value: item.value, label: item.label }))
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchRecords} loading={loading}>
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={records}
          pagination={{ pageSize: 10, showSizeChanger: true }}
        />
      </Card>

      <Modal
        title="审核入校申请"
        open={reviewModalOpen}
        onCancel={() => {
          setReviewModalOpen(false);
          setReviewingRecord(null);
          setMigrationPreview(null);
          setReviewMigrationMode('none');
          setSelectedClassIds([]);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText="提交审核"
        cancelText="取消"
        width={760}
      >
        {reviewingRecord && (
          <>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 20 }}>
              <Descriptions.Item label="真实姓名">
                <ApplicantIdentity record={reviewingRecord} compact />
              </Descriptions.Item>
              <Descriptions.Item label="登录账号">
                {reviewingRecord.applicant_username || reviewingRecord.applicant_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="当前学校" span={2}>
                {reviewingRecord.current_school_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="目标学校" span={2}>
                {reviewingRecord.requested_school_name}
              </Descriptions.Item>
              <Descriptions.Item label="目标地区" span={2}>
                {formatAreaText(
                  reviewingRecord.requested_province,
                  reviewingRecord.requested_city,
                  reviewingRecord.requested_county
                ) || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="详细地址" span={2}>
                {reviewingRecord.requested_address || <Text type="secondary">未填写</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="联系电话">
                {reviewingRecord.contact_phone || <Text type="secondary">未填写</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="申请说明">
                {reviewingRecord.reason || <Text type="secondary">未填写</Text>}
              </Descriptions.Item>
            </Descriptions>

            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="默认安全审核"
              description="默认只把申请人加入目标学校，不退出旧学校、不隐藏旧班级、不迁移历史数据。需要迁移时请先核对下方影响预览，再手动选择迁移。"
            />

            <Card
              size="small"
              loading={migrationPreviewLoading}
              title="迁移影响预览"
              style={{ marginBottom: 20 }}
            >
              {migrationPreview ? (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Descriptions size="small" column={3}>
                    <Descriptions.Item label="可迁移班级">
                      {migrationPreview.totals?.migrationEligibleClasses ?? migrationPreview.totals?.classes ?? 0}
                    </Descriptions.Item>
                    <Descriptions.Item label="涉及学生">
                      {migrationPreview.totals?.students || 0}
                    </Descriptions.Item>
                    <Descriptions.Item label="共享班级">
                      {migrationPreview.totals?.sharedClasses || 0}
                    </Descriptions.Item>
                  </Descriptions>
                  {(migrationPreview.warnings || []).length > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      message="迁移警告"
                      description={(migrationPreview.warnings || []).join('；')}
                    />
                  )}
                  {(migrationPreview.duplicateClasses || []).length > 0 && (
                    <Alert
                      type="error"
                      showIcon
                      message="存在同名班级冲突"
                      description="目标学校已有同名班级，暂不能执行迁移。请先处理重名班级后再迁移。"
                    />
                  )}
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    rowSelection={reviewMigrationMode === 'move_teacher_classes' ? {
                      selectedRowKeys: selectedClassIds,
                      onChange: (keys) => setSelectedClassIds(keys.map(String)),
                      getCheckboxProps: (record) => {
                        const duplicate = (migrationPreview.duplicateClasses || []).some(
                          (item) => String(item.sourceClassId || item.source_class_id) === String(record.id)
                        );
                        return { disabled: record.migrationEligible === false || duplicate };
                      }
                    } : undefined}
                    columns={[
                      { title: '班级', dataIndex: 'name', key: 'name' },
                      { title: '来源学校', dataIndex: 'schoolName', key: 'schoolName' },
                      { title: '学生数', dataIndex: 'studentCount', key: 'studentCount', width: 90 },
                      { title: '其他教师', dataIndex: 'otherTeacherCount', key: 'otherTeacherCount', width: 90 }
                    ]}
                    dataSource={migrationPreview.classes || []}
                    locale={{ emptyText: '没有需要迁移的班级数据' }}
                  />
                  {(migrationPreview.tableCounts || []).length > 0 && (
                    <Text type="secondary">
                      关联数据：{migrationPreview.tableCounts.map((item) => `${item.label}${item.count}`).join('，')}
                    </Text>
                  )}
                </Space>
              ) : (
                <Text type="secondary">暂无迁移预览，审核将按安全模式只变更用户归属。</Text>
              )}
            </Card>

            <Form form={form} layout="vertical" onFinish={handleReview}>
              <Form.Item
                name="status"
                label="审核结果"
                rules={[{ required: true, message: '请选择审核结果' }]}
              >
                <Select options={REVIEW_OPTIONS} />
              </Form.Item>

              <Form.Item name="resolved_school_name" label="最终归属学校名称">
                <Input placeholder="可填写最终确认的规范学校名称；留空则默认按申请学校处理" />
              </Form.Item>

              <Form.Item
                name="migration_mode"
                label="数据处理方式"
                tooltip="默认不迁移数据。只有确认这些班级确实应归入目标学校时，才选择迁移。"
              >
                <Select
                  onChange={setReviewMigrationMode}
                  options={[
                    { value: 'none', label: '只变更用户归属，不迁移班级数据' },
                    {
                      value: 'move_teacher_classes',
                      label: '迁移所选班级及关联数据到目标学校',
                      disabled: Boolean(migrationPreview && selectedClassIds.length === 0)
                    }
                  ]}
                />
              </Form.Item>

              <Form.Item name="review_note" label="审核备注">
                <TextArea
                  rows={4}
                  placeholder="可填写审核判断依据、补充说明或驳回原因"
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      <Modal
        title="撤回入校审核"
        open={revokeModalOpen}
        onCancel={() => {
          setRevokeModalOpen(false);
          setRevokingRecord(null);
          revokeForm.resetFields();
        }}
        onOk={() => revokeForm.submit()}
        confirmLoading={saving}
        okText="确认撤回"
        okButtonProps={{ danger: true }}
        cancelText="取消"
      >
        {revokingRecord && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              type="warning"
              showIcon
              message="撤回说明"
              description="撤回只取消本次审核带来的目标学校教师归属，不删除原数据，也不回滚已执行的数据迁移。已迁移的数据需要平台管理员单独回滚。"
            />
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="真实姓名">
                <ApplicantIdentity record={revokingRecord} compact />
              </Descriptions.Item>
              <Descriptions.Item label="目标学校">
                {revokingRecord.requested_school_name || '-'}
              </Descriptions.Item>
            </Descriptions>
            <Form form={revokeForm} layout="vertical" onFinish={handleRevoke}>
              <Form.Item
                name="revoke_note"
                label="撤回原因"
                rules={[{ required: true, message: '请填写撤回原因' }]}
              >
                <TextArea rows={4} maxLength={300} showCount placeholder="请填写撤回原因，便于后续追溯" />
              </Form.Item>
            </Form>
          </Space>
        )}
      </Modal>
    </Space>
  );
};

export default SchoolAffiliationCorrectionReview;
